import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { base_url } from "../../utils/AxiosConfig";
import UserList from "./UserList";
import CloneUser from "./CloneUser";

const CreateUser = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("-");
  const [roleListFromAPI, setRoleListFromAPI] = useState(null);
  const [employee, setEmployee] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [employeeListFromAPI, setEmployeeListFromAPI] = useState(null);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [currentUserToBeEdited, setCurrentUserToBeEdited] = useState(null); // null = create mode
  const [statusMessage, setStatusMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [showClonePopup, setShowClonePopup] = useState(false);
  const [clickedUserFromList, setClickedUserFromList] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // const disableUserSave = !username || !password || !role || role === "-";
  const disableUserSave = !username || !password || !employee;

  const userDataArrayFromLocal = JSON.parse(
    localStorage.getItem("BondTimeuserDataArray") || "null",
  );
  const COMPANY_CODE = userDataArrayFromLocal?.[0]?.cmpcode;

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${base_url}/api/CpaysCount/${COMPANY_CODE}/USERLIST/-/-`,
      );
      console.log("User List:", response.data);
      setUsers(response.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!COMPANY_CODE) return;
    getRoles();
    getEmployeeList();
    fetchUsers();
  }, [COMPANY_CODE]);

  const getEmployeeList = async () => {
    try {
      const response = await axios.get(
        `${base_url}/api/PersonalInfoList/${COMPANY_CODE}/ALL/YES/ALL/ALL/ALL/ALL`,
      );
      setEmployeeListFromAPI(response.data);
    } catch (error) {
      console.error("Error while fetching Employee list!", error);
    }
  };

  const getRoles = async () => {
    try {
      const response = await axios.get(
        `${base_url}/api/ClientMenuControl/${COMPANY_CODE}/${COMPANY_CODE}/ROLES/-`,
      );
      setRoleListFromAPI(response.data);
    } catch (error) {
      console.error("Error while fetching Roles list!", error);
    }
  };

  const employeeRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (employeeRef.current && !employeeRef.current.contains(event.target)) {
        setShowEmployeeDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Called from UserList when edit button is clicked
  const handleEditUser = (user) => {
    setCurrentUserToBeEdited(user);
    setUsername(user.loginame || "");
    setPassword(user.loginpwd || "");
    setEmployee(user.empcode || "");
    setEmployeeSearch(user.empcode || "");
    setEmployeeId(
      employeeListFromAPI?.find(
        (e) => e.Name?.toLowerCase() === user.empcode?.toLowerCase(),
      )?.EmpId || "",
    );
    setStatusMessage("");
    // find matching role case-insensitively
    const matchedRole = roleListFromAPI?.find(
      (r) => r.ROLE?.toLowerCase() === user.accessgrp?.toLowerCase(),
    );
    setRole(matchedRole ? matchedRole.ROLE : user.accessgrp || "-");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setCurrentUserToBeEdited(null);
    setUsername("");
    setPassword("");
    setRole("-");
    setEmployee("");
    setEmployeeId("");
    setEmployeeSearch("");
    setStatusMessage("");
  };

  const onSaveUser = async () => {
    // if (role?.trim()?.toUpperCase() === "ALL" || role === "-") {
    //   alert("Please select a role.");
    //   return;
    // }

    if (!username.trim() || !password.trim() || !employee?.trim()) {
      setStatusMessage("Please enter all fields.");
      return;
    }

    // ── EDIT MODE ──────────────────────────────────────────────
    if (currentUserToBeEdited) {
      setSaving(true);
      setStatusMessage("");

      const arrayToPass = [
        {
          CmpCode: COMPANY_CODE,
          mode: "EDIT",
          loginame: username,
          loginpwd: password,
          sales_man: "-",
          accessgrp: role,
          empcode: employeeId || employee,
        },
      ];

      try {
        const response = await axios.post(
          `${base_url}/api/CRMUserReg`,
          JSON.stringify(arrayToPass),
          { headers: { "Content-Type": "application/json" } },
        );
        console.log("User updated successfully", response.data);
        setStatusMessage("User updated successfully.");
        fetchUsers();
        handleCancelEdit(); // reset to create mode after edit
      } catch (error) {
        console.error("Error while updating User", error);
        setStatusMessage("Failed to update user. Please try again.");
      } finally {
        setSaving(false);
      }
      return;
    }

    // ── CREATE MODE — duplicate check first ────────────────────
    try {
      const dupeRes = await axios.get(
        `${base_url}/api/CpaysCount/${COMPANY_CODE}/USERLIST/${username.trim()}/-`,
      );
      console.log("Duplicate check response:", dupeRes.data);

      if (dupeRes.data?.length > 0) {
        alert("Username already exists. Please choose a different username.");
        return;
      }
    } catch (error) {
      console.error("Error during duplicate check", error);
      setStatusMessage("Could not verify username. Please try again.");
      return;
    }

    setSaving(true);
    setStatusMessage("");

    const arrayToPass = [
      {
        CmpCode: COMPANY_CODE,
        mode: "ENTRY",
        loginame: username,
        loginpwd: password,
        sales_man: "-",
        accessgrp: role,
        empcode: employeeId || employee,
      },
    ];

    try {
      const response = await axios.post(
        `${base_url}/api/CRMUserReg`,
        JSON.stringify(arrayToPass),
        { headers: { "Content-Type": "application/json" } },
      );
      console.log("User created successfully", response.data);
      setStatusMessage("User created successfully.");
      fetchUsers();
      handleCancelEdit(); // reset form
    } catch (error) {
      console.error("Error while saving User", error);
      setStatusMessage("Failed to create user. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex justify-left items-center mb-6">
        <div className="w-1 h-8 bg-gradient-to-r from-[#2663EB] to-blue-400 rounded-full mr-2"></div>
        <h6 className="text-xl font-bold text-gray-900 flex items-center gap-4">
          <span className="bg-gradient-to-r from-[#2663EB] to-blue-600 bg-clip-text text-transparent">
            {currentUserToBeEdited ? "EDIT USER" : "USER CREATE"}
          </span>
        </h6>
      </div>

      <div className="bg-white border rounded p-2">
        <div className="rounded-lg flex justify-around items-center flex-1">
          {/* Username */}
          <div className="mb-2">
            <label className="form-label fs-small text-secondary">Username</label>
            <input
              placeholder="Enter Username"
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          {/* Password */}
          <div className="mb-2">
            <label className="form-label fs-small text-secondary">Password</label>
            <input
              placeholder="Enter Password"
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {/* Role */}
          {/* <div className="mb-2">
            <label className="form-label fs-small text-secondary">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="-">None</option>
              {roleListFromAPI?.map((r, index) => (
                <option key={index} value={r.ROLE}>
                  {r.ROLE}
                </option>
              ))}
            </select>
          </div> */}

          {/* Employee */}
          <div ref={employeeRef} className="mb-2 w-68 relative">
            <label className="form-label fs-small text-secondary">Employee</label>
            <input
              type="text"
              placeholder="Search Employee..."
              value={employeeSearch}
              onFocus={() => setShowEmployeeDropdown(true)}
              onChange={(e) => {
                setEmployeeSearch(e.target.value);
                setShowEmployeeDropdown(true);
              }}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {showEmployeeDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-sm max-h-60 overflow-y-auto">
                <div
                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                  onClick={() => {
                    setEmployee("");
                    setEmployeeId("");
                    setEmployeeSearch("");
                    setShowEmployeeDropdown(false);
                  }}
                >
                  None
                </div>
                {employeeListFromAPI
                  ?.filter((emp) =>
                    emp?.Name?.toLowerCase().includes(employeeSearch.toLowerCase()),
                  )
                  .map((emp, index) => (
                    <div
                      key={index}
                      className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                      onClick={() => {
                        setEmployee(emp.Name);
                        setEmployeeId(emp.EmpId);
                        setEmployeeSearch(emp.Name);
                        setShowEmployeeDropdown(false);
                      }}
                    >
                      {emp.Name}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* Status message */}
        {statusMessage && (
          <p className={`text-center text-sm mb-1 ${statusMessage.includes("success") ? "text-green-600" : "text-red-500"}`}>
            {statusMessage}
          </p>
        )}

        <div className="flex justify-center items-center gap-2">
          <button
            disabled={disableUserSave || saving}
            onClick={onSaveUser}
            className={`my-2 font-semibold flex items-center justify-center gap-1 px-3 py-1.5 text-sm text-white rounded
              ${disableUserSave || saving ? "bg-gray-300 cursor-not-allowed" : "bg-green-500 hover:bg-green-600 transition"}`}
          >
            {saving ? "Saving..." : currentUserToBeEdited ? "Update User" : "Save User"}
          </button>

          {/* Cancel button only shown in edit mode */}
          {currentUserToBeEdited && (
            <button
              onClick={handleCancelEdit}
              className="my-2 font-semibold px-3 py-1.5 text-sm text-white rounded bg-gray-400 hover:bg-gray-500 transition"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="mt-4">
        <UserList onEditUser={handleEditUser} setShowClonePopup={setShowClonePopup} setClickedUserFromList={setClickedUserFromList} users={users} loading={loading} />
      </div>

       <div
        className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-300  ${
          showClonePopup ? "" : "hidden"
        }`}
      >
        <CloneUser
          setShowClonePopup={setShowClonePopup}
          clickedUserFromList={clickedUserFromList}
        />
      </div>
    </div>
  );
};

export default CreateUser;

